import { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { PATHS } from 'src/constants/navigateRoutes';
import { getActivationGetStarted } from 'src/services/activation';
import { useAuthStore } from 'src/stores/AuthStore/AuthStore';
import type { ActivationViewerMode } from 'src/types/activation';
import type { ActivationGetStartedResponse } from 'src/types/activation';

export interface DashboardHomeActivationHandoff {
  organizationId: string;
  content: ActivationGetStartedResponse;
}

export const getDashboardHomeDestination = (
  revampExperience: boolean,
  viewerMode?: ActivationViewerMode,
): string | null => {
  if (!revampExperience) return PATHS.getStarted;
  if (!viewerMode) return null;

  return viewerMode === 'activated' ? PATHS.tasks : PATHS.getStarted;
};

/**
 * Activated revamp users open on their primary work queue. Setup and training
 * users stay in the existing activation lifecycle, while legacy organizations
 * retain their existing dashboard home exactly.
 */
const DashboardHomeRedirect = observer(() => {
  const { getUserDetails, isRevampExperience } = useAuthStore();
  const organizationId = getUserDetails()?.organizationId;
  const revampExperience = isRevampExperience();
  const [destination, setDestination] = useState<string | null>(() =>
    getDashboardHomeDestination(revampExperience),
  );
  const [activationHandoff, setActivationHandoff] =
    useState<DashboardHomeActivationHandoff | null>(null);

  useEffect(() => {
    let active = true;
    const immediateDestination = getDashboardHomeDestination(revampExperience);

    if (immediateDestination) {
      setActivationHandoff(null);
      setDestination(immediateDestination);

      return () => {
        active = false;
      };
    }

    setDestination(null);
    void getActivationGetStarted()
      .then((response) => {
        if (!active) return;

        if (!response.success || !response.content) {
          setDestination(PATHS.getStarted);

          return;
        }

        const resolvedDestination = getDashboardHomeDestination(
          revampExperience,
          response.content.viewerMode,
        );

        setActivationHandoff(
          resolvedDestination === PATHS.getStarted &&
            typeof organizationId === 'string'
            ? { organizationId, content: response.content }
            : null,
        );
        setDestination(resolvedDestination ?? PATHS.getStarted);
      })
      .catch(() => {
        if (active) setDestination(PATHS.getStarted);
      });

    return () => {
      active = false;
    };
  }, [organizationId, revampExperience]);

  if (!destination) {
    return (
      <Box
        role="status"
        aria-label="Loading dashboard"
        sx={{ minHeight: '40vh', display: 'grid', placeItems: 'center' }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Navigate
      to={destination}
      replace
      state={
        activationHandoff
          ? { dashboardHomeActivation: activationHandoff }
          : undefined
      }
    />
  );
});

export default DashboardHomeRedirect;
